import { Button, ButtonProps, CircularProgress, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ButtonMotion } from "./button-motion";
import React from "react";
import { APP_COLORS } from "../../../enums";
import { PencilSimple } from "@phosphor-icons/react";

interface LoadingButtonProps {
  loading?: boolean;
  loadingPosition?: "start" | "end";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loadingIndicator?: string;
}


function LoadingButton({ loadingPosition, loadingIndicator, loading, ...props }: ButtonProps & LoadingButtonProps) {
  return (
    <Button
      endIcon={loading && loadingPosition === "end" ? <CircularProgress color="inherit" size={15} /> : props.endIcon}
      startIcon={loading && loadingPosition === "start" ? <CircularProgress color="inherit" size={15} /> : props.startIcon}
      disabled={loading}
      {...props}
    />
  );
}

function AppButton({ loading, width, height, animate = true, ...props }: any) {
  const defaultProps = {
    type: "submit",
    size: "large",
    variant: "contained",
  };

  function renderButton() {
    if (animate) {
      return (
        <ButtonMotion disabled={props.disabled}>
          <LoadingButton loading={loading} {...defaultProps} {...props} />
        </ButtonMotion>
      );
    }
    return <LoadingButton loading={loading} {...defaultProps} {...props} />;
  }

  return renderButton();
}

const PrimaryButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps & ButtonProps
>((props, ref) => {
  return (
    <LoadingButton
      loading={props.loading}
      ref={ref}
      variant="primary"
      loadingPosition="start"
      type={props.type || "submit"}
      {...props}
    />
  );
});

PrimaryButton.displayName = "PrimaryButton";

const DefaultButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps & ButtonProps
>((props, ref) => {
  return (
    <LoadingButton
      loading={props.loading}
      ref={ref}
      variant="default"
      {...props}
    />
  );
});

const CancelButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps & ButtonProps
>((props, ref) => {
  return (
    <LoadingButton
      loading={props.loading}
      ref={ref}
      variant={props.variant || "default"}
      sx={{
        color: props.variant === "contained" ? APP_COLORS.white : APP_COLORS.error,
        border: props.variant === "contained" ? "none" : `1px solid ${APP_COLORS.error}`,
        background: props.variant === "contained" ? APP_COLORS.error : APP_COLORS.white,
        "&:hover": {
          background: props.variant === "contained" ? APP_COLORS.error : APP_COLORS.white,
        },
      }}
      {...props}
    />
  );
});

const WarningButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps & ButtonProps
>((props, ref) => {
  return (
    <LoadingButton
      loading={props.loading}
      ref={ref}
      variant={props.variant || "default"}
      sx={{
        color: props.variant === "contained" ? APP_COLORS.white : APP_COLORS.yellow,
        border: props.variant === "contained" ? "none" : `1px solid ${APP_COLORS.yellow}`,
        background: props.variant === "contained" ? APP_COLORS.yellowDark : APP_COLORS.white,
        "&:hover": {
          background: props.variant === "contained" ? APP_COLORS.yellowDark : APP_COLORS.white,
        },
      }}
      {...props}
    />
  );
});

const DeleteButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps & ButtonProps
>((props, ref) => {
  return (
    <LoadingButton
      loading={props.loading}
      ref={ref}
      variant={props.variant || "default"}
      sx={{
        color: props.variant === "contained" ? APP_COLORS.white : APP_COLORS.error,
        border: props.variant === "contained" ? "none" : `1px solid ${APP_COLORS.error}`,
        background: props.variant === "contained" ? `radial-gradient(253.73% 100% at 50% 0%, #ED3D5A 0%, #C8102E 100%)` : APP_COLORS.white,
        "&:hover": {
          background: props.variant === "contained" ? `radial-gradient(253.73% 100% at 50% 0%, #ED3D5A 0%, #C8102E 100%)` : APP_COLORS.white,
        },
      }}
      {...props}
    />
  );
});

WarningButton.displayName = "WarningButton";
DeleteButton.displayName = "DeleteButton";

interface EditButtonProps {
  onClick?: () => void;
  text?: string;
  variant?: "primary" | "default";
}

function EditButton({ onClick, text = "Edit", variant = "primary", ...props }: EditButtonProps & ButtonProps) {
  if (!onClick) return null;

  return (
    <PrimaryButton
      variant={variant as "primary" | "default"}
      onClick={onClick}
      size="small"
      startIcon={
        <PencilSimple
          size={18}
        />
      }
      {...props}
    >
      <Typography variant="body-ibmPlexMono-medium">{text}</Typography>
    </PrimaryButton>
  );
}

export { AppButton, PrimaryButton, DefaultButton, EditButton, CancelButton, WarningButton, DeleteButton };
export { ButtonMotion } from "./button-motion";

export * from "./save-cancel";
export * from "./view-all";
export * from "./request-coordination";
export { default as AmbassadorDetailsButton } from "./ambassador-details-button";
export { default as DelegateSchedulingButton } from "./delegate-scheduling-button";
export { default as DeleteIconButton } from "./delete-icon-button";
export { default as JoinMeetingButton } from "./join-meeting-button";
